import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Download, Edit3, ShieldOff, PauseCircle, Loader2, AlertTriangle, ArrowLeft, Mail,
} from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function StatusBadge({ status }) {
  const map = {
    ready: "default", processing: "secondary", requested: "secondary",
    completed: "default", failed: "destructive", expired: "outline",
    open: "secondary", closed_accepted: "default", closed_rejected: "destructive",
    closed_partial: "secondary", cancelled: "outline", downloaded: "outline",
    verified: "secondary", scheduled: "secondary", processing_active: "secondary",
  };
  return (
    <Badge data-testid={`privacy-status-${status}`} variant={map[status] || "outline"}>
      {String(status || "—").replace(/_/g, " ")}
    </Badge>
  );
}

export default function SettingsPrivacy() {
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const [tab, setTab] = useState("export");

  useEffect(() => {
    if (!authLoading && !user) navigate("/login?next=/settings/privacy", { replace: true });
  }, [user, authLoading, navigate]);

  // ---- Status --------------------------------------------------------------
  const [statusData, setStatusData] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/privacy/requests/status");
      setStatusData(r.data);
    } catch (e) {
      toast.error("Could not load your requests.");
    } finally {
      setStatusLoading(false);
    }
  }, []);
  useEffect(() => { if (user) loadStatus(); }, [user, loadStatus]);

  // ---- Export --------------------------------------------------------------
  const [exports_, setExports] = useState([]);
  const [pwdExport, setPwdExport] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null); // {export_id, download_token}
  const loadExports = useCallback(async () => {
    try {
      const r = await api.get("/privacy/export/status");
      setExports(r.data.exports || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (user) loadExports(); }, [user, loadExports]);

  const submitExport = useCallback(async () => {
    if (!pwdExport) { toast.error("Please re-enter your password."); return; }
    setExportBusy(true);
    try {
      const r = await api.post("/privacy/export/request", { password: pwdExport });
      setPendingDownload({
        export_id: r.data.export_id,
        download_token: r.data.download_token,
        size_bytes: r.data.size_bytes,
        truncated: r.data.truncated,
        expires_at: r.data.expires_at,
      });
      setPwdExport("");
      toast.success("Your export is ready to download.");
      loadExports();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "reauth_failed" ? "Password did not match."
        : d === "export_rate_limited" ? "You've reached your daily export limit."
        : "Could not create export.");
    } finally { setExportBusy(false); }
  }, [pwdExport, loadExports]);

  const doDownload = useCallback(async (export_id, token) => {
    try {
      const r = await api.get(`/privacy/export/download/${encodeURIComponent(export_id)}`,
        { params: { token }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `stockaily-export-${export_id}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      setPendingDownload(null);
      loadExports();
    } catch {
      toast.error("Download failed or link has expired.");
    }
  }, [loadExports]);

  // ---- Rectification ------------------------------------------------------
  const [rectColl, setRectColl] = useState("users");
  const [rectField, setRectField] = useState("");
  const [rectValue, setRectValue] = useState("");
  const [rectDesc, setRectDesc] = useState("");
  const [rectRows, setRectRows] = useState([]);
  const [rectBusy, setRectBusy] = useState(false);

  const loadRects = useCallback(async () => {
    try {
      const r = await api.get("/privacy/rectification/status");
      setRectRows(r.data.requests || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { if (user) loadRects(); }, [user, loadRects]);

  const submitRect = useCallback(async () => {
    if (!rectField || !rectValue) {
      toast.error("Field and requested value are required.");
      return;
    }
    setRectBusy(true);
    try {
      await api.post("/privacy/rectification/request", {
        target_collection: rectColl,
        target_field: rectField,
        requested_value_code: rectValue,
        disputed_description_code: rectDesc || null,
      });
      toast.success("Rectification request submitted.");
      setRectField(""); setRectValue(""); setRectDesc("");
      loadRects();
      loadStatus();
    } catch {
      toast.error("Could not submit rectification request.");
    } finally { setRectBusy(false); }
  }, [rectColl, rectField, rectValue, rectDesc, loadRects, loadStatus]);

  // ---- Erasure ------------------------------------------------------------
  const [showErasure, setShowErasure] = useState(false);
  const [erasurePwd, setErasurePwd] = useState("");
  const [erasureConfirm, setErasureConfirm] = useState("");
  const [erasureBusy, setErasureBusy] = useState(false);

  const submitErasure = useCallback(async () => {
    if (erasureConfirm.trim().toUpperCase() !== "DELETE MY ACCOUNT") {
      toast.error("Please type the exact confirmation phrase.");
      return;
    }
    setErasureBusy(true);
    try {
      const r = await api.post("/privacy/erasure/request", {
        password: erasurePwd,
        confirm_delete_phrase: erasureConfirm.trim().toUpperCase(),
      });
      toast.success(r.data.reused_existing
        ? "You already have an active deletion request."
        : "Deletion request created. You can still cancel before processing begins.");
      setShowErasure(false);
      setErasurePwd(""); setErasureConfirm("");
      loadStatus();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "reauth_failed" ? "Password did not match."
        : d === "confirm_phrase_mismatch" ? "Confirmation phrase must be exact."
        : "Could not create deletion request.");
    } finally { setErasureBusy(false); }
  }, [erasurePwd, erasureConfirm, loadStatus]);

  const cancelErasure = useCallback(async () => {
    const active = statusData?.active_erasure_request;
    if (!active) return;
    try {
      await api.post("/privacy/erasure/cancel", { request_id: active.request_id });
      toast.success("Deletion request cancelled.");
      loadStatus();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "past_irreversible_boundary"
        ? "Deletion has already begun and cannot be cancelled."
        : "Could not cancel deletion.");
    }
  }, [statusData, loadStatus]);

  // ---- Restriction --------------------------------------------------------
  const [restrictReason, setRestrictReason] = useState("");
  const submitRestrict = useCallback(async () => {
    if (!restrictReason) { toast.error("Please provide a short reason code."); return; }
    try {
      await api.post("/privacy/restriction/request", { reason_code: restrictReason });
      toast.success("Restriction request received. An Owner will review.");
      setRestrictReason(""); loadStatus();
    } catch { toast.error("Could not submit restriction request."); }
  }, [restrictReason, loadStatus]);

  if (authLoading) return null;
  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="settings-privacy-page">
      <NavBar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
                  data-testid="privacy-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-semibold" data-testid="privacy-heading">
            Privacy &amp; your data
          </h1>
        </div>

        <Alert className="mb-6" data-testid="privacy-provisional-notice">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Provisional wording</AlertTitle>
          <AlertDescription>
            The controls on this page let you exercise your data rights. Some
            business, accounting or security records may remain retained or
            restricted where required. This copy is factual and will be
            finalised in a later workstream aligning legal and CMP wording.
          </AlertDescription>
        </Alert>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            ["export", "Download my data", Download],
            ["rectification", "Correct my data", Edit3],
            ["communications", "Email preferences", Mail],
            ["erasure", "Delete my account", ShieldOff],
            ["restriction", "Restrict processing", PauseCircle],
          ].map(([id, label, Icon]) => (
            <Button key={id} variant={tab === id ? "default" : "outline"}
                    size="sm" onClick={() => setTab(id)}
                    data-testid={`privacy-tab-${id}`}>
              <Icon className="w-4 h-4 mr-1" /> {label}
            </Button>
          ))}
        </div>

        {/* ---- Export tab ---- */}
        {tab === "export" && (
          <Card data-testid="privacy-export-card">
            <CardHeader>
              <CardTitle>Download my data</CardTitle>
              <CardDescription>
                We&apos;ll build a JSON export of your account, watchlist,
                portfolio and billing-record business fields. Sensitive fields
                (password hashes, session tokens, TOTP secrets, raw webhook
                payloads, IPs, full user agents) are excluded.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="pwd-export">Re-enter your password</Label>
                  <Input id="pwd-export" type="password" value={pwdExport}
                         onChange={e => setPwdExport(e.target.value)}
                         autoComplete="current-password"
                         data-testid="privacy-export-password-input" />
                </div>
                <Button onClick={submitExport} disabled={exportBusy}
                        data-testid="privacy-export-submit-btn">
                  {exportBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Request export
                </Button>
              </div>
              {pendingDownload && (
                <Alert data-testid="privacy-export-ready-alert">
                  <AlertTitle>Export ready</AlertTitle>
                  <AlertDescription>
                    Size: {pendingDownload.size_bytes} bytes. Expires {fmtDate(pendingDownload.expires_at)}.
                    {pendingDownload.truncated && (<em> Note: content was truncated to fit the size limit.</em>)}
                    <div className="mt-2">
                      <Button onClick={() => doDownload(pendingDownload.export_id, pendingDownload.download_token)}
                              data-testid="privacy-export-download-btn">
                        <Download className="w-4 h-4 mr-1" /> Download JSON
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}
              <Separator />
              <div>
                <h4 className="font-medium mb-2">Previous exports</h4>
                {exports_.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="privacy-exports-empty">
                    No exports yet.
                  </p>
                )}
                <ul className="space-y-1 text-sm" data-testid="privacy-exports-list">
                  {exports_.map(x => (
                    <li key={x.export_id} className="flex flex-wrap gap-3 items-center">
                      <StatusBadge status={x.status} />
                      <span className="text-muted-foreground">created {fmtDate(x.created_at)}</span>
                      <span className="text-muted-foreground">expires {fmtDate(x.expires_at)}</span>
                      <span className="text-muted-foreground">downloads: {x.download_count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ---- Communications / Email Preferences tab ---- */}
        {tab === "communications" && (
          <EmailCommunicationsPanel data-testid="privacy-communications-card" />
        )}

        {/* ---- Rectification tab ---- */}
        {tab === "rectification" && (
          <Card data-testid="privacy-rectification-card">
            <CardHeader>
              <CardTitle>Correct my data</CardTitle>
              <CardDescription>
                Ask us to correct data you cannot edit yourself. We&apos;ll open
                a case and reply with our decision.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="rect-coll">Data area</Label>
                  <Input id="rect-coll" value={rectColl}
                         onChange={e => setRectColl(e.target.value)}
                         data-testid="privacy-rect-collection-input" />
                </div>
                <div>
                  <Label htmlFor="rect-field">Field</Label>
                  <Input id="rect-field" value={rectField}
                         onChange={e => setRectField(e.target.value)}
                         placeholder="e.g. name"
                         data-testid="privacy-rect-field-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="rect-value">Requested correction</Label>
                <Input id="rect-value" value={rectValue}
                       onChange={e => setRectValue(e.target.value)}
                       data-testid="privacy-rect-value-input" />
              </div>
              <div>
                <Label htmlFor="rect-desc">Short explanation (optional)</Label>
                <Textarea id="rect-desc" value={rectDesc} maxLength={1024}
                          onChange={e => setRectDesc(e.target.value)}
                          data-testid="privacy-rect-desc-input" />
              </div>
              <Button onClick={submitRect} disabled={rectBusy}
                      data-testid="privacy-rect-submit-btn">
                {rectBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                Submit request
              </Button>
              <Separator />
              <h4 className="font-medium">Your rectification requests</h4>
              {rectRows.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="privacy-rects-empty">
                  None yet.
                </p>
              )}
              <ul className="text-sm space-y-1" data-testid="privacy-rects-list">
                {rectRows.map(r => (
                  <li key={r.request_id} className="flex flex-wrap gap-3 items-center">
                    <StatusBadge status={r.status} />
                    <span>{r.target_collection}.{r.target_field}</span>
                    <span className="text-muted-foreground">{fmtDate(r.opened_at)}</span>
                    {r.decision_code && <Badge variant="outline">{r.decision_code}</Badge>}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* ---- Erasure tab ---- */}
        {tab === "erasure" && (
          <Card data-testid="privacy-erasure-card">
            <CardHeader>
              <CardTitle>Delete my account</CardTitle>
              <CardDescription>
                We&apos;ll remove your personal account data and terminate active
                sessions. Some limited billing, accounting, security or
                review-required records may be retained or restricted where
                required. You can cancel from this page until deletion begins.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {statusData?.active_erasure_request ? (
                <Alert data-testid="privacy-active-erasure-alert">
                  <AlertTitle>Active deletion request</AlertTitle>
                  <AlertDescription>
                    Status: <StatusBadge status={statusData.active_erasure_request.status} /> —
                    requested {fmtDate(statusData.active_erasure_request.requested_at)}.
                    <div className="mt-2">
                      <Button variant="outline" onClick={cancelErasure}
                              data-testid="privacy-erasure-cancel-btn">
                        Cancel deletion
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <Button variant="destructive" onClick={() => setShowErasure(true)}
                        data-testid="privacy-erasure-open-btn">
                  Start deletion request
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* ---- Restriction tab ---- */}
        {tab === "restriction" && (
          <Card data-testid="privacy-restriction-card">
            <CardHeader>
              <CardTitle>Restrict processing / privacy request</CardTitle>
              <CardDescription>
                Use this to request that we pause processing of your account
                data while we handle another privacy request or an unresolved
                question.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="restrict-reason">Short reason code</Label>
                <Input id="restrict-reason" value={restrictReason}
                       onChange={e => setRestrictReason(e.target.value)}
                       placeholder="e.g. disputed_data"
                       data-testid="privacy-restriction-reason-input" />
              </div>
              <Button onClick={submitRestrict}
                      data-testid="privacy-restriction-submit-btn">
                Submit
              </Button>
              {statusData?.restricted_processing && (
                <Alert data-testid="privacy-currently-restricted-alert">
                  <AlertTitle>Your account is currently restricted</AlertTitle>
                  <AlertDescription>
                    Product features are paused pending Owner review.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Combined status */}
        <Card className="mt-8" data-testid="privacy-status-card">
          <CardHeader>
            <CardTitle>Request status</CardTitle>
            <CardDescription>Every request you&apos;ve submitted.</CardDescription>
          </CardHeader>
          <CardContent>
            {statusLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {!statusLoading && (statusData?.cases?.length || 0) === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="privacy-status-empty">
                No requests yet.
              </p>
            )}
            <ul className="text-sm space-y-1" data-testid="privacy-cases-list">
              {(statusData?.cases || []).map(c => (
                <li key={c.case_id} className="flex flex-wrap items-center gap-3">
                  <StatusBadge status={c.status} />
                  <span>{c.case_type.replace(/_/g, " ")}</span>
                  <span className="text-muted-foreground">opened {fmtDate(c.opened_at)}</span>
                  {c.closed_at && <span className="text-muted-foreground">closed {fmtDate(c.closed_at)}</span>}
                  {c.decision_code && <Badge variant="outline">{c.decision_code}</Badge>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </main>

      <Dialog open={showErasure} onOpenChange={setShowErasure}>
        <DialogContent data-testid="privacy-erasure-dialog">
          <DialogHeader>
            <DialogTitle>Delete my account</DialogTitle>
            <DialogDescription>
              This is the irreversible boundary. Once deletion begins we cannot
              undo it. Please re-enter your password and type the confirmation
              phrase exactly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="erasure-pwd">Password</Label>
              <Input id="erasure-pwd" type="password" value={erasurePwd}
                     onChange={e => setErasurePwd(e.target.value)}
                     autoComplete="current-password"
                     data-testid="privacy-erasure-password-input" />
            </div>
            <div>
              <Label htmlFor="erasure-confirm">Type: DELETE MY ACCOUNT</Label>
              <Input id="erasure-confirm" value={erasureConfirm}
                     onChange={e => setErasureConfirm(e.target.value)}
                     data-testid="privacy-erasure-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowErasure(false)}
                    data-testid="privacy-erasure-dialog-cancel-btn">
              Cancel
            </Button>
            <Button variant="destructive" onClick={submitErasure} disabled={erasureBusy}
                    data-testid="privacy-erasure-dialog-confirm-btn">
              {erasureBusy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Request deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Email Communications Panel (Slice 3)
// ---------------------------------------------------------------------------
function EmailCommunicationsPanel(props) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/account/email-preferences");
      setState(r?.data || null);
    } catch (_e) {
      setErr("load_failed");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  async function call(path, body) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(path, body || {});
      setState(r?.data || null);
    } catch (_e) {
      toast.error("Could not update your email preferences.");
    } finally {
      setBusy(false);
    }
  }

  const isSubscribed = state?.state === "subscribed";
  const topics = state?.topics || {};
  const suppressed = Boolean(state?.deliverability_suppressed);

  return (
    <Card data-testid={props["data-testid"] || "privacy-communications-card"}>
      <CardHeader>
        <CardTitle>Email preferences</CardTitle>
        <CardDescription>
          Choose which Stockaily emails you receive. Security emails
          (password reset, account changes) are always sent regardless
          of these settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
            Loading…
          </div>
        ) : err ? (
          <Alert>
            <AlertTitle>Preferences unavailable</AlertTitle>
            <AlertDescription>
              We couldn&apos;t load your preferences. Please try again shortly.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {suppressed && (
              <Alert data-testid="privacy-communications-suppressed">
                <AlertTitle>Delivery suppressed</AlertTitle>
                <AlertDescription>
                  Your address is currently on our delivery-suppression
                  list (bounce or complaint on record). Marketing emails
                  will not be sent even if you subscribe until this is
                  resolved.
                </AlertDescription>
              </Alert>
            )}
            <div className="rounded-lg border p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">
                    Stockaily marketing emails
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Explicit opt-in only. Never checked by default.
                    You can unsubscribe at any time.
                  </div>
                </div>
                <div>
                  {isSubscribed ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => call("/account/email-preferences/unsubscribe")}
                      data-testid="privacy-communications-unsubscribe-btn"
                    >
                      Unsubscribe
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() =>
                        call("/account/email-preferences/subscribe", {
                          topics: ["newsletter", "product_updates"],
                        })
                      }
                      data-testid="privacy-communications-subscribe-btn"
                    >
                      Subscribe
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="rounded-lg border">
              <TopicRow
                testid="privacy-communications-topic-newsletter"
                label="Stockaily newsletter"
                desc="Occasional research summaries and market thoughts."
                enabled={Boolean(topics.newsletter)}
                disabled={!isSubscribed || busy}
                onToggle={(v) =>
                  call("/account/email-preferences/topic", {
                    topic: "newsletter",
                    enabled: v,
                  })
                }
              />
              <Separator />
              <TopicRow
                testid="privacy-communications-topic-product_updates"
                label="Product updates"
                desc="New features, changelog and reliability updates."
                enabled={Boolean(topics.product_updates)}
                disabled={!isSubscribed || busy}
                onToggle={(v) =>
                  call("/account/email-preferences/topic", {
                    topic: "product_updates",
                    enabled: v,
                  })
                }
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Consent version: {state?.text_version || "v1"}. Marketing
              consent is independent of Terms/Privacy acceptance and can
              be changed at any time.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TopicRow({ testid, label, desc, enabled, disabled, onToggle }) {
  return (
    <label
      className="flex items-start gap-3 p-3 cursor-pointer"
      data-testid={testid}
    >
      <input
        type="checkbox"
        checked={enabled}
        disabled={disabled}
        onChange={(e) => onToggle(e.target.checked)}
        className="mt-1 h-4 w-4"
        data-testid={`${testid}-input`}
      />
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
